import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useObserver, useSubtree } from "subtree.js/react";
import { useModalLayer } from "../../components/ModalLayer";
import { formatAmount } from "../../core/primitives/money";
import { TerminalPaymentActions, TerminalPaymentState } from "./terminal-payment-controller";

/**
 * Takes payment for a customer cart on a Stripe Terminal reader, over the page
 * and over Checkout when it opens from there. The page controller connects on
 * open. Escape does what the first button says: Cancel while the reader waits
 * for the card, nothing while the card is processing, else Close or Done.
 */
export function TerminalPaymentModal({
  customerCartId,
  fallbackTotalCents,
}: {
  customerCartId: string;
  /** Minor units. Shown before the server has priced the cart. */
  fallbackTotalCents: number;
}) {
  const state = useSubtree(TerminalPaymentState);
  const actions = useSubtree(TerminalPaymentActions);
  const observer = useObserver();
  const stage = observer.watch(state.stage);
  const connectedReader = observer.watch(state.connectedReader);
  const amountTotal = observer.watch(state.amountTotal);
  const currency = observer.watch(state.currency);
  const error = observer.watch(state.error);
  const said = observer.watch(state.said);

  const collecting = stage === "collecting";
  const processing = stage === "processing";
  const paid = stage === "succeeded";
  const amount = amountTotal !== null ? formatAmount(amountTotal, currency) : formatAmount(fallbackTotalCents, "USD");
  const dismiss = collecting ? () => void actions.cancel() : processing ? () => {} : actions.close;
  // One button through every stage, so the focus stays on it when Close turns into Done.
  const first = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  useModalLayer(first, dismiss);
  // Charge card goes once the card is paid for, and the focus with it.
  useEffect(() => {
    if (paid) first.current?.focus();
  }, [paid]);

  return createPortal(
    <div className="scrim items-center justify-center p-4">
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="modal w-full max-w-md p-6">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 id={titleId} className="text-base font-semibold text-white">
              Take payment
            </h2>
            <p className="text-xs text-muted">Card reader · Stripe Terminal</p>
          </div>
          <span className="text-lg font-bold text-accent">{amount}</span>
        </div>

        <div role="status">
          {stage === "connecting" && <p className="note px-4 py-6 text-center text-muted">Looking for a reader…</p>}

          {connectedReader && !paid && (
            <p className="mb-3 rounded-btn bg-surface px-3 py-2 text-xs text-muted">
              Connected to <span className="font-medium text-white">{connectedReader.label}</span>
            </p>
          )}

          {/* Every live stage of the transaction wears the accent — this is the one
            screen where the merchant is watching for the reader to respond, and
            splitting "ready" from "collecting" by colour would say nothing that
            the sentence does not. */}
          {stage === "ready" && (
            <p className="note note-accent px-4 py-6 text-center text-accent">Ready. Charge the card when you are.</p>
          )}

          {stage === "collecting" && (
            <p className="note note-accent px-4 py-6 text-center text-accent">Present the card on the reader…</p>
          )}

          {stage === "processing" && (
            <p className="note note-accent px-4 py-6 text-center text-accent">Processing — do not remove the card.</p>
          )}

          {paid && (
            <p className="note note-accent px-4 py-6 text-center font-medium text-accent">
              Paid. The cart shows as paid once Stripe confirms it, usually within seconds.
            </p>
          )}
        </div>

        {error && (
          <p key={said} role="alert" className="note note-danger mt-3 text-danger">
            {error}
          </p>
        )}

        <div className="mt-5 flex gap-2">
          <button
            ref={first}
            type="button"
            onClick={dismiss}
            aria-disabled={processing || undefined}
            className={`${paid ? "btn-primary" : "btn-secondary"} btn-sm flex-1 aria-disabled:cursor-default aria-disabled:opacity-50`}
          >
            {paid ? "Done" : collecting ? "Cancel" : processing ? "Processing…" : "Close"}
          </button>
          {/* Still focusable while the reader works, so a second press lands here and does nothing. */}
          {!paid && (
            <button
              type="button"
              onClick={() => {
                if (stage === "ready") void actions.collect(customerCartId);
              }}
              aria-disabled={stage !== "ready" || undefined}
              className="btn-primary btn-sm flex-1 aria-disabled:cursor-default aria-disabled:opacity-50"
            >
              Charge card
            </button>
          )}
        </div>

        {stage === "failed" && (
          <div className="mt-3 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => void actions.connect()}
              className="text-xs font-medium text-white underline"
            >
              Search for readers again
            </button>
            <button
              type="button"
              onClick={() => void actions.connect({ simulated: true })}
              className="text-xs text-faint underline"
            >
              Use a simulated reader (testing)
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
